import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import type {
  UnitCustomField,
  CustomFieldType,
} from "@ippon/shared-types";
import { toApiErrorPayload } from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

interface UnitCustomFieldState {
  list: {
    data: UnitCustomField[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
}

const initialState: UnitCustomFieldState = {
  list: { data: [], loading: false, error: null, loaded: false },
};

// ── Thunks ────────────────────────────────────────────

export const fetchUnitCustomFields = createAsyncThunk<UnitCustomField[]>(
  "unitCustomFields/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/unit-custom-fields`);
      return res.data.data.items as UnitCustomField[];
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(error, "カスタムフィールドの取得に失敗しました"),
      );
    }
  },
);

export const createUnitCustomField = createAsyncThunk<
  UnitCustomField,
  {
    label: string;
    field_type: CustomFieldType;
    options?: string[];
    display_order?: number;
    section_key?: import("@ippon/shared-types").UnitCustomFieldSection;
  }
>("unitCustomFields/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/unit-custom-fields`,
      body,
    );
    return res.data.data as UnitCustomField;
  } catch (error: any) {
    // BE のエラーメッセージ (例: 409 「同じセクションに同名の項目が既に存在します」) を表示
    const msg =
      error?.response?.data?.message ||
      "カスタムフィールドの登録に失敗しました";
    return rejectWithValue(msg);
  }
});

export const updateUnitCustomField = createAsyncThunk<
  UnitCustomField,
  {
    field_id: string;
    body: {
      label?: string;
      field_type?: CustomFieldType;
      options?: string[];
      display_order?: number;
      section_key?: import("@ippon/shared-types").UnitCustomFieldSection;
    };
  }
>(
  "unitCustomFields/update",
  async ({ field_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/unit-custom-fields/${field_id}`,
        body,
      );
      return res.data.data as UnitCustomField;
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        "カスタムフィールドの更新に失敗しました";
      return rejectWithValue(msg);
    }
  },
);

export const deleteUnitCustomField = createAsyncThunk<
  string,
  { field_id: string }
>("unitCustomFields/delete", async ({ field_id }, { rejectWithValue }) => {
  try {
    await ipponApi.delete(
      `${BaseURL}/api/v1/unit-custom-fields/${field_id}`,
    );
    return field_id;
  } catch (error: any) {
    const msg =
      error?.response?.data?.message ||
      "カスタムフィールドの削除に失敗しました";
    return rejectWithValue(msg);
  }
});

// ── Slice ─────────────────────────────────────────────

const unitCustomFieldSlice = createSlice({
  name: "unitCustomFields",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnitCustomFields.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchUnitCustomFields.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
        state.list.loaded = true;
      })
      .addCase(fetchUnitCustomFields.rejected, (state, action) => {
        state.list.loading = false;
        const payload = action.payload as
          | { message?: string }
          | string
          | undefined;
        state.list.error =
          typeof payload === "string"
            ? payload
            : payload?.message ?? "カスタムフィールドの取得に失敗しました";
      })
      .addCase(createUnitCustomField.fulfilled, (state, action) => {
        state.list.data.push(action.payload);
      })
      .addCase(updateUnitCustomField.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (f) => f.field_id === action.payload.field_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
      })
      .addCase(deleteUnitCustomField.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (f) => f.field_id !== action.payload,
        );
      });
  },
});

export default unitCustomFieldSlice.reducer;
